import { createPortal } from "react-dom";
/**
 * Select - ドロップダウンメニュー
 *
 * MUIのMenuと同様の設計：
 * - 呼び出し側がトリガーボタンを自由にデザイン
 * - anchorRefで位置を指定
 * - Portal を使用してドロップダウンを body に描画
 * - useScaleでスケール対応
 * - 画面下端に近い場合は自動的に上方向に表示
 */
import { useRef, useEffect, useCallback, useState } from "react";
import { useScale } from "common/utils";

export interface SelectOption<T> {
  value: T;
  label: string;
}

export interface SelectProps<T> {
  anchorRef: React.RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  value: T | null;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  direction: "down" | "up";
}

const DROPDOWN_MAX_HEIGHT = 240;
const OPTION_HEIGHT = 51;
const GAP = 4;

export const Select = <T extends string | number>({
  anchorRef,
  open,
  onClose,
  value,
  onChange,
  options,
}: SelectProps<T>) => {
  const { scale } = useScale();
  const optionsRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<DropdownPosition | null>(null);

  // ドロップダウン位置を計算
  const calculatePosition = useCallback((): DropdownPosition => {
    if (!anchorRef.current) {
      return { top: 0, left: 0, width: 120, direction: "down" };
    }

    const rect = anchorRef.current.getBoundingClientRect();
    // 実際のドロップダウン高さを計算（選択肢の数 × 各選択肢の高さ、最大240px）
    const actualHeight = Math.min(options.length * OPTION_HEIGHT, DROPDOWN_MAX_HEIGHT);
    const dropdownHeight = actualHeight * scale;
    const gapScaled = GAP * scale;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // 下に十分なスペースがあるか、上より下のスペースが大きい場合は下に表示
    const showBelow = spaceBelow >= dropdownHeight + gapScaled || spaceBelow >= spaceAbove;

    if (showBelow) {
      return {
        top: rect.bottom + gapScaled,
        left: rect.left,
        width: rect.width / scale,
        direction: "down",
      };
    } else {
      return {
        top: rect.top - dropdownHeight - gapScaled,
        left: rect.left,
        width: rect.width / scale,
        direction: "up",
      };
    }
  }, [anchorRef, scale, options.length]);

  // openが変わったときに位置を計算
  useEffect(() => {
    if (open) {
      setPosition(calculatePosition());
    }
  }, [open, calculatePosition]);

  // 外部クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedAnchor = anchorRef.current?.contains(target);
      const clickedOptions = optionsRef.current?.contains(target);
      if (!clickedAnchor && !clickedOptions) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, anchorRef, onClose]);

  const handleSelect = (option: SelectOption<T>) => {
    onChange(option.value);
    // 遅延して閉じる（選択状態を見せるため）
    setTimeout(() => onClose(), 200);
  };

  if (!open || !position || position.top === 0) return null;

  return createPortal(
    <div
      ref={optionsRef}
      className="fixed z-[999999] max-h-[240px] overflow-y-auto bg-white border-2 border-green-alt rounded-[8px] shadow-lg origin-top-left"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        minWidth: 120,
        transform: `scale(${scale})`,
      }}
    >
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <div
            key={String(option.value)}
            onClick={() => handleSelect(option)}
            className={`
              py-2 px-4 text-[24px] cursor-pointer select-none
              ${isSelected ? "bg-green-alt text-white" : "text-textcolor-black"}
              ${!isSelected ? "active:bg-green-20" : ""}
            `}
          >
            {option.label}
          </div>
        );
      })}
    </div>,
    document.body,
  );
};
